import React from 'react';
import { useLocation, useParams, Link, Navigate } from 'react-router-dom';
import './ProjectDetail.css';
import { projectsData } from './Projects';
import projectCatalogModule from '../../content/projectCatalog';
import { normalizeSafeUrl } from '../../utils/safeUrl';

const { getProjectPath } = projectCatalogModule;

const getProjectMetaTags = (project) => [
  project.periodLabel ? { type: 'period', label: project.periodLabel } : null,
  ...(project.firstTerms || []).map((term) => ({ type: 'first', label: term })),
].filter(Boolean).slice(0, 3);

const getProjectNeighbors = (project) => {
  const currentIndex = projectsData.findIndex((candidate) => candidate.slug === project.slug);

  return {
    previousProject: currentIndex > 0 ? projectsData[currentIndex - 1] : null,
    nextProject: currentIndex < projectsData.length - 1 ? projectsData[currentIndex + 1] : null,
  };
};

const getRelatedProjects = (project) => {
  const currentIndex = projectsData.findIndex((candidate) => candidate.slug === project.slug);
  const firstTerms = new Set(project.firstTerms || []);

  return projectsData
    .filter((candidate) => candidate.slug !== project.slug)
    .map((candidate, index) => {
      const sharesPathway = candidate.detail?.pathway === project.detail?.pathway;
      const sharesTerm = (candidate.firstTerms || []).some((term) => firstTerms.has(term));
      const distance = Math.abs(index - currentIndex);

      return {
        project: candidate,
        score: (sharesPathway ? 100 : 0) + (sharesTerm ? 40 : 0) - distance,
      };
    })
    .sort((a, b) => b.score - a.score || projectsData.indexOf(a.project) - projectsData.indexOf(b.project))
    .slice(0, 3)
    .map(({ project: relatedProject }) => relatedProject);
};

const ProjectTags = ({ project }) => {
  const tags = getProjectMetaTags(project);

  if (!tags.length) {
    return null;
  }

  return (
    <div className="project-detail__tags" aria-label="Project classifications">
      {tags.map((tag) => (
        <span className={`project-detail-tag project-detail-tag--${tag.type}`} key={`${tag.type}-${tag.label}`}>
          {tag.label}
        </span>
      ))}
    </div>
  );
};

const ProjectProfile = ({ project }) => {
  const firstRole = project.firstTerms?.length ? project.firstTerms.join(', ') : null;

  return (
    <aside className="project-profile" aria-labelledby="project-profile-heading">
      <h2 id="project-profile-heading">Project Profile</h2>
      <dl>
        {project.periodLabel && (
          <div className="project-profile__item">
            <dt>Period</dt>
            <dd>{project.periodLabel}</dd>
          </div>
        )}
        <div className="project-profile__item">
          <dt>System pathway</dt>
          <dd>{project.detail.pathway}</dd>
        </div>
        {firstRole && (
          <div className="project-profile__item">
            <dt>FIRST role</dt>
            <dd>{firstRole}</dd>
          </div>
        )}
      </dl>
    </aside>
  );
};

const RelatedProject = ({ project }) => (
  <article className="project-related-card">
    <Link to={getProjectPath(project)} aria-label={`View related project: ${project.title}`}>
      <span>{project.detail?.pathway}</span>
      <strong>{project.title}</strong>
      <em>View project &rarr;</em>
    </Link>
  </article>
);

const ProjectResources = ({ resources }) => {
  const safeResources = (resources || [])
    .map((resource) => ({
      ...resource,
      href: normalizeSafeUrl(resource.href),
    }))
    .filter((resource) => resource.href);

  if (!safeResources.length) {
    return null;
  }

  return (
    <section className="project-resources" aria-labelledby="project-resources-heading">
      <h2 id="project-resources-heading">Project Resources</h2>
      <div className="project-resource-list">
        {safeResources.map((resource) => (
          <a
            key={resource.label}
            className="project-resource-link"
            href={resource.href}
            download={resource.download}
            aria-label={`${resource.label}. Open resource`}
          >
            <span>
              <strong>{resource.label}</strong>
              <small>{resource.description}</small>
            </span>
            <em>Open resource &rarr;</em>
          </a>
        ))}
      </div>
    </section>
  );
};

const ProjectDetail = () => {
  const { projectIdOrSlug } = useParams();
  const { pathname } = useLocation();
  const project = projectsData.find(
    (candidate) => candidate.slug === projectIdOrSlug || String(candidate.id) === projectIdOrSlug
  );

  if (!project) {
    return (
      <main className="project-detail-page">
        <div className="project-not-found">
          <h2>Project not found</h2>
          <Link to="/projects" className="project-detail-button">Back to Projects</Link>
        </div>
      </main>
    );
  }

  const canonicalPath = getProjectPath(project);
  if (pathname !== canonicalPath) {
    return <Navigate to={canonicalPath} replace />;
  }

  const { previousProject, nextProject } = getProjectNeighbors(project);
  const relatedProjects = getRelatedProjects(project);

  return (
    <main className="project-detail-page">
      <article className="project-detail-shell">
        <header className="project-detail-hero">
          <nav className="project-breadcrumb" aria-label="Project breadcrumb">
            <Link to="/projects">Projects</Link>
            <span aria-hidden="true">/</span>
            <span>{project.title}</span>
          </nav>

          <ProjectTags project={project} />

          <div className="project-detail-hero__copy">
            <h1>{project.title}</h1>
            <p>{project.detail.lead || project.shortDescription}</p>
          </div>

          <div className="project-detail-hero__media">
            <figure className={`project-detail-hero__image${project.imageFit === 'contain' ? ' project-detail-hero__image--contain' : ''}`}>
              <img src={project.image} alt={`${project.title} project visual`} />
            </figure>

            <ProjectProfile project={project} />
          </div>
        </header>

        <div className="project-detail-main">
          <div className="project-narrative">
            <section aria-labelledby="project-challenge-heading">
              <h2 id="project-challenge-heading">The Challenge</h2>
              <p>{project.detail.challenge}</p>
            </section>

            <section aria-labelledby="project-response-heading">
              <h2 id="project-response-heading">INTEGRA&apos;s Response</h2>
              <p>{project.detail.response}</p>
            </section>

            <section aria-labelledby="project-evidence-heading">
              <h2 id="project-evidence-heading">Evidence</h2>
              <ul className="project-evidence-list" aria-label={`${project.title} evidence`}>
                {project.detail.evidence.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>

            <section aria-labelledby="project-outcome-heading">
              <h2 id="project-outcome-heading">Why It Matters</h2>
              <p>{project.detail.outcome}</p>
            </section>

            <section aria-labelledby="project-continuity-heading">
              <h2 id="project-continuity-heading">Continuity</h2>
              <p>{project.detail.continuity}</p>
            </section>
          </div>
        </div>

        <ProjectResources resources={project.resources} />

        <section className="project-related" aria-labelledby="project-related-heading">
          <div className="project-detail-section-heading">
            <h2 id="project-related-heading">Related Projects</h2>
          </div>
          <div className="project-related-grid" role="list" aria-label="Related projects">
            {relatedProjects.map((relatedProject) => (
              <div role="listitem" key={relatedProject.slug}>
                <RelatedProject project={relatedProject} />
              </div>
            ))}
          </div>
        </section>

        <nav className="project-detail-nav" aria-label="Project navigation">
          <Link to="/projects" className="project-detail-button project-detail-button--primary">
            &larr; Back to All Projects
          </Link>

          <div className="project-detail-nav__neighbors">
            {previousProject && (
              <Link to={getProjectPath(previousProject)} aria-label={`Previous project: ${previousProject.title}`}>
                <span>Previous</span>
                <strong>{previousProject.title}</strong>
              </Link>
            )}
            {nextProject && (
              <Link to={getProjectPath(nextProject)} aria-label={`Next project: ${nextProject.title}`}>
                <span>Next</span>
                <strong>{nextProject.title}</strong>
              </Link>
            )}
          </div>
        </nav>
      </article>
    </main>
  );
};

export default ProjectDetail;
